import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { Drawer, Avatar, Text } from '@Sage/hoi-poi-ui';
import { COMPANIES, CONTACTS } from 'constants/Entities';
import { EntityListSelectActions } from 'actions';
import ConfirmModal from 'components/ConfirmModal';
import { getLiteral, getLiteralWithParameters } from 'utils/getLiteral';
import { successToast, errorToast } from 'utils/toast';
import { publish } from 'lib/EventBuser';
import { REFRESH_TABLE, UPDATE_ROW } from 'lib/events';
import DrawerTabs from 'components/DrawerTabs';
import { intercomHideAndShow } from 'utils/intercom';
import { userflowHideAndShow } from 'utils/userflow';
import useGiveConsent from './useGiveConsent';
import CompaniesTab from './CompaniesTab';
import ContactsTab from './ContactsTab';

import './styles.scss';

const TAB_COMPANIES = 'companies';
const TAB_CONTACTS = 'contacts';

const DRAWER_CONTENT_STYLES = { maxWidth: '85vw', height: '100%' };
const CONFIRM_MODAL_CLOSE_TIMEOUT = 250;

const TabTitle = ({ icon, label }) => (
    <div className="fm-subscribers-panel__tab-title">
        <Avatar icon={icon} className="fm-subscribers-panel__tab-avatar" />
        <Text isTruncated>{label}</Text>
    </div>
);

const TAB_ENTITY_MAP = {
    [TAB_COMPANIES]: COMPANIES,
    [TAB_CONTACTS]: CONTACTS,
};

const Subscribers = memo(
    ({ onClose, setActiveEntity, resetListSelect, setShouldClearWeb5Checkboxes }) => {
        const giveConsent = useGiveConsent();
        const clearPayloadTimeoutRef = useRef(null);
        const [confirmState, setConfirmState] = useState({
            isOpen: false,
            isSubmitting: false,
            payload: null,
        });

        const schedulePayloadCleanup = useCallback(() => {
            clearTimeout(clearPayloadTimeoutRef.current);
            clearPayloadTimeoutRef.current = setTimeout(() => {
                setConfirmState((prev) => (prev.isOpen ? prev : { ...prev, payload: null }));
            }, CONFIRM_MODAL_CLOSE_TIMEOUT);
        }, []);

        useEffect(() => {
            userflowHideAndShow('hide');
            intercomHideAndShow('hide');
            return () => {
                clearTimeout(clearPayloadTimeoutRef.current);
                userflowHideAndShow('show');
                intercomHideAndShow('show');
            };
        }, []);

        const handleTabChange = useCallback(
            (key) => {
                const entity = TAB_ENTITY_MAP[key];
                if (entity) setActiveEntity(entity);
            },
            [setActiveEntity],
        );

        const handleOpenBulkSubscribedConfirm = useCallback((payload) => {
            if (!payload?.entity || !payload?.filterKey) return;

            clearTimeout(clearPayloadTimeoutRef.current);

            setConfirmState({
                isOpen: true,
                isSubmitting: false,
                payload,
            });
        }, []);

        const handleCloseBulkSubscribedConfirm = useCallback(() => {
            if (confirmState.isSubmitting) return;

            setConfirmState((prev) => ({
                ...prev,
                isOpen: false,
            }));
            schedulePayloadCleanup();
        }, [confirmState.isSubmitting, schedulePayloadCleanup]);

        const handleConfirmBulkSubscribed = useCallback(() => {
            const payload = confirmState.payload;
            if (!payload?.entity || !payload?.filterKey) return;

            setConfirmState((prev) => ({ ...prev, isSubmitting: true }));

            giveConsent(payload.entity, payload.filterKey)
                .then(() => {
                    (payload.selectedIds || []).forEach((id) => {
                        publish(`${UPDATE_ROW}_${payload.entity.entity}`, {
                            id,
                            fields: {
                                MarketingCommunications: 1,
                                marketingStatus: 1,
                            },
                        });
                    });

                    publish(`${REFRESH_TABLE}_${payload.filterKey}`);
                    resetListSelect(payload.entity, true);
                    setShouldClearWeb5Checkboxes(payload.entity, true);

                    setConfirmState((prev) => ({
                        ...prev,
                        isOpen: false,
                        isSubmitting: false,
                    }));
                    successToast({
                        title: getLiteral(
                            'title_toast_marketing_campaigns_marketing_status_update_success',
                        ),
                        text: getLiteral(
                            'label_toast_marketing_campaigns_marketing_status_update_success_message',
                        ),
                    });
                    schedulePayloadCleanup();
                })
                .catch(() => {
                    errorToast({
                        title: getLiteral(
                            'title_toast_marketing_campaigns_marketing_status_update_error',
                        ),
                        text: getLiteral(
                            'label_toast_marketing_campaigns_marketing_status_update_error_message',
                        ),
                    });
                })
                .finally(() => {
                    setConfirmState((prev) => ({ ...prev, isSubmitting: false }));
                });
        }, [
            confirmState.payload,
            giveConsent,
            resetListSelect,
            schedulePayloadCleanup,
            setShouldClearWeb5Checkboxes,
        ]);

        const tabs = useMemo(
            () => [
                {
                    key: TAB_COMPANIES,
                    title: <TabTitle icon="accounts" label={getLiteral('label_accounts')} />,
                    content: (
                        <CompaniesTab
                            onRequestBulkSubscribedConfirm={handleOpenBulkSubscribedConfirm}
                        />
                    ),
                },
                {
                    key: TAB_CONTACTS,
                    title: <TabTitle icon="contacts" label={getLiteral('label_contacts')} />,
                    content: (
                        <ContactsTab
                            onRequestBulkSubscribedConfirm={handleOpenBulkSubscribedConfirm}
                        />
                    ),
                },
            ],
            [handleOpenBulkSubscribedConfirm],
        );

        return (
            <Drawer
                isOpen={true}
                onRequestClose={onClose}
                className="fm-subscribers-panel"
                width="1150px"
                contentStyles={DRAWER_CONTENT_STYLES}
                shouldCloseOnEsc={true}
                shouldCloseOnOverlayClick={false}
            >
                <DrawerTabs
                    className="fm-subscribers-panel__inner"
                    panelsClassName="fm-subscribers-panel__panels"
                    tabs={tabs}
                    defaultActiveTab={TAB_COMPANIES}
                    onClose={onClose}
                    onTabChange={handleTabChange}
                />
                <ConfirmModal
                    width="540px"
                    isOpen={confirmState.isOpen}
                    title={getLiteral(confirmState.payload?.title || '')}
                    confirmText={getLiteral(confirmState.payload?.confirmLiteral)}
                    cancelText={getLiteral('action_cancel')}
                    onConfirm={handleConfirmBulkSubscribed}
                    onClose={handleCloseBulkSubscribedConfirm}
                    closeTimeoutMS={CONFIRM_MODAL_CLOSE_TIMEOUT}
                    isLoading={confirmState.isSubmitting}
                >
                    {confirmState.payload?.introLiteral && (
                        <Text>
                            {getLiteralWithParameters(
                                confirmState.payload.introLiteral,
                                confirmState.payload.introParams || [],
                            )}
                        </Text>
                    )}
                    {(confirmState.payload?.bodyLiterals || []).map((literal, index) => (
                        <Text key={`${literal}-${index}`}>{getLiteral(literal)}</Text>
                    ))}
                </ConfirmModal>
            </Drawer>
        );
    },
);

const mapDispatchToProps = (dispatch) => ({
    setActiveEntity: bindActionCreators(EntityListSelectActions, dispatch).setActive,
    resetListSelect: bindActionCreators(EntityListSelectActions, dispatch).resetListSelect,
    setShouldClearWeb5Checkboxes: bindActionCreators(EntityListSelectActions, dispatch)
        .setShouldClearWeb5Checkboxes,
});

export default connect(null, mapDispatchToProps)(Subscribers);
